/* eslint-disable no-param-reassign */
import { createSlice } from "@reduxjs/toolkit";
import { set, uniqBy } from "lodash";

import { OPPORTUNITY_DATE_BASIS } from "../../components/commonComponents/OpportunityDateBasisDropdown";

// Lead engagement statuses surfaced by the BE — Activity Center's quick
// filters + Lead Engagement dashboard tiles drill into these values via
// `status` / `quickFilters`. Keep this list as the single source of truth
// so dashboard, activity center, and BE all agree on the same string keys.
export const QUICK_FILTERS = {
  MY_CONVERSATIONS: "MY_CONVERSATIONS",
  NO_RESPONSE_NEEDED: "NO_RESPONSE_NEEDED",
  ENGAGED: "ENGAGED",
  UNCONTACTED: "UNCONTACTED",
  NOT_RESPONDED: "NOT_RESPONDED",
  RESPONDED: "RESPONDED",
  NOT_ENGAGED: "NOT_ENGAGED",
  WAITING_FOR_REPLY: "WAITING_FOR_REPLY",
  NEEDS_YOUR_REPLY: "NEEDS_YOUR_REPLY",
};

export const DEFAULT_DATE_FILTER_TYPE = OPPORTUNITY_DATE_BASIS.UPDATED;

// Per-status color palette consumed by both the Activity Center lead-list
// chips (`chipClass` — Tailwind bg + text) and the ActivityCenterFilter >
// Quick filters tab dots (`dot` — hex). One place to tweak both surfaces.
export const QUICK_FILTER_COLORS = {
  [QUICK_FILTERS.MY_CONVERSATIONS]: {
    chipClass: "bg-[#EDE9FE] text-[#7C3AED]",
    dot: "#7C3AED",
  },
  [QUICK_FILTERS.UNCONTACTED]: {
    chipClass: "bg-[#FEF3C7] text-[#D97706]",
    dot: "#D97706",
  },
  [QUICK_FILTERS.NOT_RESPONDED]: {
    chipClass: "bg-rose-100 text-rose-600",
    dot: "#E11D48",
  },
  [QUICK_FILTERS.RESPONDED]: {
    chipClass: "bg-orange-100 text-orange-600",
    dot: "#FA8C16",
  },
  [QUICK_FILTERS.NOT_ENGAGED]: {
    chipClass: "bg-slate-100 text-slate-600",
    dot: "#475569",
  },
  [QUICK_FILTERS.ENGAGED]: {
    chipClass: "bg-green-100 text-green-600",
    dot: "#52C41A",
  },
  [QUICK_FILTERS.WAITING_FOR_REPLY]: {
    chipClass: "bg-blue-100 text-blue-600",
    dot: "#1677FF",
  },
  [QUICK_FILTERS.NEEDS_YOUR_REPLY]: {
    chipClass: "bg-teal-100 text-teal-600",
    dot: "#0D9488",
  },
  [QUICK_FILTERS.NO_RESPONSE_NEEDED]: {
    chipClass: "bg-[#FEF2F2] text-[#DC2626]",
    dot: "#DC2626",
  },
};

export const getQuickFilterChipClass = id => QUICK_FILTER_COLORS[id]?.chipClass || "";
export const getQuickFilterDotColor = id => QUICK_FILTER_COLORS[id]?.dot || "#999";

const initialState = {
  filters: {
    campaignFilters: [],
    userFilters: [],
    quickFilters: [],
    opportunityFilter: "",
    addMyConversation: true,
    opportunityTypeFilters: [],
    opportunityStatusFilters: [],
    opportunitySourceFilters: [],
    opportunityTypeFilterCount: 0,
    opportunityStatusFilterCount: 0,
    opportunitySourceFilterCount: 0,
    // Activity Center team scope — list of team ids. Combines with
    // `userFilters` (AND): both can be present (teams + a specific user),
    // either alone, or neither (no scope).
    teamFilters: [],
    // Activity Center date scope. `dateFilterType` is one of the
    // DATE_FILTER_OPTIONS values (TODAY, LAST_30_DAYS, …, CUSTOM) or "" for
    // no filter. `dateRange` is the ISO [start, end] pair, populated only
    // when `dateFilterType === "CUSTOM"`.
    dateFilterType: null,
    dateRange: [],
    // Activity Center date-basis scope — whether the dateFilterType /
    // dateRange filter is applied against lead created_date or
    // updated_date. Values come from OPPORTUNITY_DATE_BASIS so the
    // FE/BE token is shared with the opportunity-side filter. Default
    // is "Created on".
    dateType: DEFAULT_DATE_FILTER_TYPE,
  },
  // True while the user is viewing the Activity Center after drilling
  // in from a dashboard tile. Drives the "including deactivated users"
  // banner so the deactivated-users caveat is visible only in that
  // context. Cleared on user-driven filter interactions and on any
  // navigation out of the drill-in.
  cameFromDashboard: false,
  // Activity Center "Save filter" baseline: a copy of `filters` as it was when
  // the user last applied a preset or saved a filter. The Save button surfaces
  // only when `filters` diverges from this. Kept in redux (not component state)
  // so it survives leaving/returning to the Activity Center tab — otherwise the
  // baseline would reset on unmount while `filters` persist, wrongly surfacing
  // the Save button on return (EAI-1506).
  appliedFilterSnapshot: null,
  leadSort: "lastMessage",
  // Activity Center search scope. true = "All Leads" (search ignores active
  // filters → skipFilter:true). false = "Within Filtered Lead" (search runs
  // within the applied-filter result set → skipFilter:false). Defaults to
  // "All Leads" to match prior behavior where every search ignored filters.
  searchAllLeads: true,
  leads: [],
  leadScheduledMessages: [],
  selectedLeadId: null,
  selectedLead: {},
  leadCountChange: {},
  search: "",
  leadCenterFilters: {
    search: "",
  },
  showAll: {},
  messageOpportunity: "#DEFAULTOPPID",
  translatedContent: [],
  textMode: "Text",
  selectedEmail: null,
  selectedText: null,
};

// Helper function to reset selected lead messages and mark as not loaded in redux
const resetSelectedLeadMessagesHelper = state => {
  if (state.selectedLead && state.selectedLead?.id) {
    state.selectedLead.messages = [];

    // Also reset hasLoadedinRedux flag in the leads array for this lead
    // const leadIndex = state?.leads?.findIndex(item => +item.id === +state.selectedLead.id);
    // if (leadIndex !== -1) {
    //   state.leads[leadIndex].hasLoadedinRedux = false;
    // }
  }
};
function moveElementToFirst(arr, n) {
  if (n >= 0 && n < arr.length) {
    const elementToMove = arr.splice(n, 1)[0];
    arr.unshift(elementToMove);
  } else {
    console.error("Invalid index provided.");
  }
}

const leadSlice = createSlice({
  name: "leads",
  initialState,
  reducers: {
    setLeadsRedux(state, action) {
      state.leads = action.payload;
    },
    setCampaignFilters(state, action) {
      state.filters.campaignFilters = action.payload;
    },
    setSelectedLeadRedux(state, action) {
      resetSelectedLeadMessagesHelper(state);
      state.selectedLead = action.payload;
    },
    // Merge fresh lead-detail fields (from getLeadDetail) into the currently
    // selected lead WITHOUT touching ``messages``. Doing the merge here means
    // it always runs against the latest Redux state — callers don't pass (and
    // can't accidentally clobber with a stale snapshot) the loaded thread. The
    // id guard drops a late response that arrived after the user switched lead.
    mergeLeadDetail(state, action) {
      const leadDetail = action.payload;
      if (!leadDetail?.id) return;
      const currentId = state.selectedLead?.id;
      // Stale-response guard: drop only when we've already moved on to a
      // DIFFERENT lead. When there's no current lead yet (empty selectedLead)
      // we still populate it.
      if (currentId != null && +currentId !== +leadDetail.id) return;
      const messages = state.selectedLead?.messages || [];
      state.selectedLead = { ...state.selectedLead, ...leadDetail, messages };
    },
    pinLeadToTop(state, action) {
      state.selectedLead = action.payload;
      const findIndex = state.leads.findIndex(item => item.id === action.payload.id);
      if (findIndex !== -1) {
        // lead was found in the lead list itself
        moveElementToFirst(state.leads, findIndex);
      } else {
        // lead was not found, insert the new lead to first position
        state.leads.unshift(action.payload);
      }
    },

    setSearchRedux(state, action) {
      state.search = decodeURIComponent(action.payload);
    },
    updateLeadInList(state, action) {
      const lead = action.payload;
      const index = state.leads.findIndex(item => +item.id === +lead.id);
      if (index !== -1) {
        state.leads[index] = {
          ...state.leads[index],
          ...lead,
        };
      }
    },
    setSelectedLeadIdRedux(state, action) {
      resetSelectedLeadMessagesHelper(state);
      state.selectedLeadId = action.payload;
    },
    setUserFilters(state, action) {
      state.filters.userFilters = action.payload;
    },
    addMessage(state, action) {
      if (action?.payload?.id) {
        const index = state.leads.findIndex(item => +item.id === +action.payload.id);
        if (index !== -1 && action?.payload?.messages) {
          if (Array.isArray(action.payload.messages)) {
            state.leads[index].messages = uniqBy(action.payload.messages, message =>
              String(message.id)
            );
            state.leads[index].hasLoadedinRedux = true;
          } else if (index >= 0) {
            const messages = [...state.leads[index].messages];
            messages.unshift(action.payload.messages);
            state.leads[index].messages = uniqBy(messages, message => String(message.id));
          }
        }
      }
    },
    /**
     * Phase 3a: when the SocketIO ``sent_message`` event arrives carrying a
     * persisted Message produced by the messaging_service lambda, find the
     * optimistic ``type: "queued"`` bubble in the lead's message list and
     * replace it with the real persisted Message.
     *
     * Match is intentionally loose: same lead_id + same direction
     * (TO_LEAD) + same content body. Two bubbles with the same content
     * within seconds is a double-click; SQS-side dedup catches it BE-side
     * (same content hash + same minute = silently dropped). If we don't
     * find a queued bubble we still append the message — the SocketIO
     * event is the source of truth for the persisted row.
     */
    replaceQueuedMessage(state, action) {
      const { leadId, message } = action.payload || {};
      if (!leadId || !message) return;
      const index = state.leads.findIndex(item => +item.id === +leadId);
      if (index === -1) return;
      const lead = state.leads[index];
      if (!Array.isArray(lead.messages)) return;
      // Find first matching queued bubble: same lead, same direction.
      // For MMS, prefer first mediaUrl (uniquely identifying since the FE
      // uploaded it) — the lambda's oversize fallback rewrites content by
      // appending a gallery short URL, so content equality is unreliable.
      // For SMS, fall back to content match.
      const incomingFirstMediaUrl =
        (Array.isArray(message.mmsObjects) && message.mmsObjects[0]?.mediaUrl) || null;
      const queuedIdx = lead.messages.findIndex(m => {
        if (!m || (m.type !== "queued" && m.type !== "new")) return false;
        if (m.id) return false; // already persisted, skip
        const mDir = m.direction || "TO_LEAD";
        const evDir = message.direction || "TO_LEAD";
        if (mDir !== evDir) return false;
        const mFirstMediaUrl = (Array.isArray(m.mmsObjects) && m.mmsObjects[0]?.mediaUrl) || null;
        if (incomingFirstMediaUrl && mFirstMediaUrl) {
          return mFirstMediaUrl === incomingFirstMediaUrl;
        }
        return String(m.content || "").trim() === String(message.content || "").trim();
      });
      if (queuedIdx >= 0) {
        const next = [...lead.messages];
        next[queuedIdx] = { ...message, type: undefined };
        state.leads[index].messages = uniqBy(next, m => String(m.id || `${m.type}-${m.dateSent}`));
      } else {
        const next = [...lead.messages, { ...message, type: undefined }];
        state.leads[index].messages = uniqBy(next, m => String(m.id || `${m.type}-${m.dateSent}`));
      }
    },
    setTeamFilters(state, action) {
      state.filters.teamFilters = action.payload;
    },
    setDateFilterType(state, action) {
      state.filters.dateFilterType = action.payload || null;
    },
    setLeadDateRange(state, action) {
      state.filters.dateRange = Array.isArray(action.payload) ? action.payload : [];
    },
    setLeadDateType(state, action) {
      state.filters.dateType = action.payload || DEFAULT_DATE_FILTER_TYPE;
    },
    setCameFromDashboard(state, action) {
      state.cameFromDashboard = !!action.payload;
    },
    setLeadSortOrder(state, action) {
      state.leadSort = action.payload;
    },
    setSearchAllLeads(state, action) {
      state.searchAllLeads = action.payload;
    },
    addQuickFilter(state, action) {
      if (!state.filters.quickFilters.includes(action.payload)) {
        state.filters.quickFilters.push(action.payload);
      }
    },
    removeQuickFilter(state, action) {
      state.filters.quickFilters = state.filters.quickFilters.filter(
        item => item !== action.payload
      );
    },
    /**
     *
     * @param {*} state
     * @param {{payload: String[]}} action
     */
    setQuickFilters(state, action) {
      state.filters.quickFilters = action.payload;
    },
    // Replace the Activity Center filter set with a saved/preset filter
    // payload (parsed from `genericFilterJson.filterJson`). Merges over the
    // current filters so any key the saved payload omits keeps its value —
    // the serialized payload is the whole `state.lead.filters` object, so a
    // saved filter fully re-populates the panel + query on apply. Ignore
    // anything that isn't a plain object (legacy/malformed records store an
    // array) so a bad payload can't corrupt the filter shape.
    applyLeadFilters(state, action) {
      const { payload } = action;
      if (!payload || typeof payload !== "object" || Array.isArray(payload)) return;
      // Replace (not merge) so an applied preset defines the ENTIRE filter set:
      // any filter the preset omits resets to its default instead of leaking
      // from the previously-applied state. Merging left stale filters behind and
      // also made the "Save filter" button falsely surface right after applying
      // (the applied set no longer matched the preset). See SavedFiltersFooter.
      state.filters = { ...initialState.filters, ...payload };
      // The freshly-applied preset is the clean baseline for the Save button.
      // Clone so later edits to `filters` don't also mutate the snapshot.
      state.appliedFilterSnapshot = { ...state.filters };
    },
    // Mark the current filter set as the clean baseline (used after the user
    // saves a filter, so the Save button hides again until they change it).
    setAppliedFilterSnapshot(state, action) {
      state.appliedFilterSnapshot = action.payload;
    },
    replaceQuickFilters(state, action) {
      if (!action.payload.includes("MY_CONVERSATION")) {
        state.filters.addMyConversation = false;
      }
      state.filters.quickFilters = action.payload;
    },
    removeMyConversationFlag(state, action) {
      state.filters.addMyConversation = false;
    },
    setOpportunityFilters(state, action) {
      state.filters.opportunityFilter = action.payload;
    },
    setOpportunityTypeFilters(state, action) {
      state.filters.opportunityTypeFilters = action.payload;
    },
    setOpportunityStatusFilters(state, action) {
      state.filters.opportunityStatusFilters = action.payload;
    },
    setOpportunitySourceFilters(state, action) {
      state.filters.opportunitySourceFilters = action.payload;
    },
    setOpportunityTypeFilterCount(state, action) {
      state.filters.opportunityTypeFilterCount = action.payload;
    },
    setOpportunityStatusFilterCount(state, action) {
      state.filters.opportunityStatusFilterCount = action.payload;
    },
    setOpportunitySourceFilterCount(state, action) {
      state.filters.opportunitySourceFilterCount = action.payload;
    },
    resetLeadFilters(state, action) {
      state.filters = {
        campaignFilters: [],
        userFilters: [],
        quickFilters: [],
        opportunityFilter: "",
        addMyConversation: false,
        opportunityTypeFilters: [],
        opportunityStatusFilters: [],
        opportunitySourceFilters: [],
        opportunityTypeFilterCount: 0,
        opportunityStatusFilterCount: 0,
        opportunitySourceFilterCount: 0,
        teamFilters: [],
        dateFilterType: null,
        dateRange: [],
        dateType: DEFAULT_DATE_FILTER_TYPE,
        search: "",
      };
      // No filters applied → no baseline to compare against.
      state.appliedFilterSnapshot = null;
    },
    setScheduledMessages(state, action) {
      state.leadScheduledMessages = action.payload;
    },
    updateScheduledMessageRedux(state, action) {
      const { index, message } = action.payload;
      state.leadScheduledMessages[index] = { ...message };
    },
    addScheduledMessage(state, action) {
      const newMessage = action.payload;
      state.leadScheduledMessages.push(newMessage);
    },
    removeScheduledMessage: (state, action) => {
      const indexToRemove = action.payload;
      state.leadScheduledMessages.splice(indexToRemove, 1);
    },
    setLeadCountChange(state, action) {
      state.leadCountChange = action.payload;
    },
    deleteNoteRedux(state, action) {
      state.selectedLead = {
        ...state.selectedLead,
        leadNotes: state.selectedLead.leadNotes.filter(item => +item.id !== +action.payload),
      };
    },
    updateLeadNoteRedux(state, action) {
      const temp = state.selectedLead.leadNotes;
      const index = temp.findIndex(item => +item.id === +action.payload.id);
      if (index !== -1) {
        temp[index] = action.payload;
      }
      state.selectedLead = {
        ...state.selectedLead,
        leadNotes: temp,
      };
    },
    setLeadCenterFilters(state, action) {
      state.leadCenterFilters = {
        search: action.payload.search,
      };
    },
    resetLeadCenterFilters(state, action) {
      state.leadCenterFilters = {
        search: "",
      };
    },
    setShowAll(state, action) {
      state.showAll = action.payload.showAll;
    },
    updateLeadAddressRedux(state, action) {
      const addressIndex = state.selectedLead.addresses.findIndex(
        address => +address.id === +action.payload.id
      );
      if (addressIndex !== -1) {
        const { addresses } = state.selectedLead;
        addresses[addressIndex] = action.payload;
        state.selectedLead = { ...state.selectedLead, addresses };
      } else {
        const { addresses } = state.selectedLead;
        state.selectedLead = { ...state.selectedLead, addresses: addresses.push(action.payload) };
      }
    },
    createLeadAddressRedux(state, action) {
      state.selectedLead = { ...state.selectedLead, addresses: [action.payload] };
    },
    changeMessageOpportunity(state, action) {
      state.messageOpportunity = action.payload;
    },
    setTranlatedContent(state, action) {
      const temp = state.translatedContent;
      temp.push(action.payload);
      state.translatedContent = temp;
    },
    resetTranlatedContent(state, action) {
      state.translatedContent = [];
    },
    setAutoResponseCampaign(state, action) {
      state.selectedLead.campaignAutoResponse = action.payload;
    },
    setTextMode(state, action) {
      state.textMode = action.payload;
    },
    setselectedEmail(state, action) {
      state.selectedEmail = action.payload;
    },
    setselectedText(state, action) {
      state.selectedText = action.payload;
    },
    setMessageFeedback(state, action) {
      const findIndex = state.selectedLead.messages.findIndex(
        item => +item.id === +action.payload.messageId
      );
      if (findIndex !== -1) {
        state.selectedLead = {
          ...state.selectedLead,
          messages: state.selectedLead.messages.map(item => {
            if (+item.id === +action.payload.messageId) {
              return {
                ...item,
                messageFeedback: action.payload.feedback,
                messageFeedbackNote: action.payload.feedbackNote || "",
                messageFeedbackOptionSelection: action.payload.feedbackOptionIds || [],
              };
            }
            return item;
          }),
        };
      }
      state.messageFeedback = action.payload;

      // Update the messages in the leads array (equivalent to what addMessage does)
      if (state.selectedLead?.id) {
        const index = state.leads.findIndex(item => +item.id === +state.selectedLead.id);
        if (index !== -1 && state.selectedLead.messages) {
          state.leads[index].messages = state.selectedLead.messages;
        }
      }
    },
    updateOtto360OverviewRedux(state, action) {
      const { leadId, otto360Overview } = action.payload;
      // Update selected lead
      if (+state.selectedLead?.id === +leadId) {
        state.selectedLead = {
          ...state.selectedLead,
          otto360Overview,
        };
      }
      // Update lead in leads array
      const leadIndex = state.leads.findIndex(lead => +lead.id === +leadId);
      if (leadIndex !== -1) {
        state.leads[leadIndex] = {
          ...state.leads[leadIndex],
          otto360Overview,
        };
      }
    },
    incrementMessageNotesCount(state, action) {
      const { messageId } = action.payload;

      if (state.selectedLead?.messages) {
        const messageIndex = state.selectedLead.messages.findIndex(msg => msg.id === messageId);
        if (messageIndex !== -1) {
          const currentMessage = state.selectedLead.messages[messageIndex];
          const currentCount = currentMessage?.messageNotesCount || 0;
          state.selectedLead.messages[messageIndex].messageNotesCount = currentCount + 1;
        }
      }
    },
    resetSelectedLeadMessages(state) {
      resetSelectedLeadMessagesHelper(state);
    },
  },
});

export const getSelectedLead = state => state.lead.selectedLead;

// Timestamp a message is ordered by — same field priority the chat renders with
// (see MessagesGroup). Falsy/unparseable dates sort to the bottom.
const getMessageTime = message => {
  const time = new Date(message?.dateReceived || message?.dateSent || 0).getTime();
  return Number.isNaN(time) ? 0 : time;
};

/**
 * The newest message on the selected lead, by timestamp.
 *
 * `selectedLead.messages` is NOT stored in chronological order: newly sent
 * messages (createMessage / MMS) are appended to the end, but scroll-back
 * pagination also appends the OLDER page to the end. So `messages[length - 1]`
 * can be an old message. (The UI still looks correct because MessagesGroup
 * sorts at render time.)
 *
 * Resolving by max timestamp here keeps this correct for both freshly loaded
 * and real-time appended messages, without changing how messages are stored.
 */
export const getLatestMessage = state => {
  const messages = state.lead.selectedLead?.messages || [];
  if (messages.length === 0) return undefined;
  return messages.reduce((latest, message) =>
    getMessageTime(message) > getMessageTime(latest) ? message : latest
  );
};

export const getLatestMessageLeadOpportunityId = state =>
  getLatestMessage(state)?.leadOpportunityId;

export const {
  setCampaignFilters,
  setUserFilters,
  setTeamFilters,
  setDateFilterType,
  setLeadDateRange,
  setLeadDateType,
  setCameFromDashboard,
  setLeadsRedux,
  addMessage,
  replaceQueuedMessage,
  setLeadSortOrder,
  setSearchAllLeads,
  addQuickFilter,
  removeQuickFilter,
  replaceQuickFilters,
  removeMyConversationFlag,
  setOpportunityFilters,
  resetLeadFilters,
  setScheduledMessages,
  updateScheduledMessageRedux,
  addScheduledMessage,
  removeScheduledMessage,
  setLeadCountChange,
  setSelectedLeadRedux,
  mergeLeadDetail,
  setSelectedLeadIdRedux,
  pinLeadToTop,
  setSearchRedux,
  deleteNoteRedux,
  updateLeadNoteRedux,
  setLeadCenterFilters,
  resetLeadCenterFilters,
  updateLeadInList,
  setShowAll,
  updateLeadAddressRedux,
  createLeadAddressRedux,
  changeMessageOpportunity,
  setQuickFilters,
  applyLeadFilters,
  setAppliedFilterSnapshot,
  setTranlatedContent,
  resetTranlatedContent,
  setTextMode,
  setAutoResponseCampaign,
  setselectedEmail,
  setselectedText,
  setMessageFeedback,
  updateOtto360OverviewRedux,
  incrementMessageNotesCount,
  resetSelectedLeadMessages,
  setOpportunityTypeFilters,
  setOpportunityStatusFilters,
  setOpportunitySourceFilters,
  setOpportunityTypeFilterCount,
  setOpportunityStatusFilterCount,
  setOpportunitySourceFilterCount,
} = leadSlice.actions;

export const getUpdatedCountInfo = state => state.lead.leadCountChange;

export default leadSlice.reducer;
